import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, BookOpen, ChevronLeft, Upload, User, LogOut, Sparkles } from 'lucide-react';
import { useSite } from '../context/SiteContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import ReadingProgressBar from './ReadingProgressBar';
import WebPushBell from './WebPushBell';

interface NavbarProps {
  previewMode?: boolean;
  onNavigate?: (path: string) => void;
  activePath?: string;
}

const Navbar: React.FC<NavbarProps> = ({ previewMode, onNavigate, activePath }) => {
  const [scrolled, setScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { pages } = useSite();
  const { t } = useLanguage();
  const { currentUser, isMember, openAuthModal, logout } = useAuth();

  const currentPath = previewMode ? activePath : location.pathname;

  useEffect(() => {
    if (previewMode) return;
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [previewMode]);

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    if (previewMode && onNavigate) {
      e.preventDefault();
      onNavigate(path);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: document.title,
          text: document.querySelector('meta[name="description"]')?.getAttribute('content') || '',
          url: window.location.href,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copiado para a área de transferência!');
    }
  };

  const handleBack = () => {
    const stateData = location.state as any;
    const isFromBookList = stateData?.fromBookList || stateData?.series;
    const storedBookListId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('last_active_booklist_id') : null;
    const bookListId = stateData?.fromBookList?.id || stateData?.series?.id || storedBookListId;
    const isArticleRoute = location.pathname.startsWith('/artigo/') || location.pathname.startsWith('/artigos/');
    const isBiluOrBookListArticle = location.pathname.includes('bilu') || location.pathname.includes('ratanaba') || Boolean(bookListId && (isFromBookList || isArticleRoute));

    if (isArticleRoute && (isFromBookList || isBiluOrBookListArticle)) {
      const targetId = bookListId || 'et-bilu-e-suas-aventuras-em-ratanaba';
      navigate(`/comunidade?subtab=booklists&booklist=${encodeURIComponent(targetId)}#indice-capitulos`);
      return;
    }

    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate(-1);
    }
  };

  const navLinks = [
    { name: t('nav.home', 'Início'), path: '/' },
    { name: t('nav.articles', 'Artigos'), path: '/artigos' },
    { name: t('nav.community', 'Comunidade'), path: '/comunidade' },
    { name: t('nav.bookstore', 'Livraria'), path: '/livraria' },
    ...pages.map(p => ({ name: p.title, path: `/p/${p.path}` }))
  ];

  const isActive = (path: string) => currentPath === path;

  return (
    <header 
      className={`${previewMode ? 'relative' : 'fixed top-0 left-0 right-0'} z-50 transition-all duration-300 ease-in-out ${
        scrolled || previewMode ? 'py-2 md:py-4 bg-[#050505]/80 backdrop-blur-2xl border-b border-white/5 shadow-[0_4px_30px_rgba(0,0,0,0.5)]' : 'py-4 md:py-6 bg-gradient-to-b from-black/60 to-transparent'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 md:pl-10 lg:pl-16 flex md:justify-between items-center relative h-12 md:h-auto justify-center">
        {/* Logo and Voltar */}
        <div className="flex items-center gap-3 md:gap-4 absolute left-4 md:static">
          {!previewMode && location.pathname !== '/' && (
            <button 
              onClick={handleBack}
              className="flex items-center justify-center w-10 h-10 md:w-auto md:h-auto rounded-full md:rounded-none bg-black/40 md:bg-transparent backdrop-blur-md md:backdrop-blur-none border border-white/10 md:border-transparent text-stone-300 hover:text-white transition-colors cursor-pointer"
              title="Voltar para a página anterior"
              aria-label="Voltar para a página anterior"
            >
              <ChevronLeft size={24} className="md:w-5 md:h-5" />
              <span className="hidden md:inline font-sans text-sm font-medium mr-2">Voltar</span>
            </button>
          )}
        </div>
        
        <Link 
          to="/" 
          onClick={(e) => handleLinkClick(e, '/')} 
          className="flex items-center gap-2 md:gap-3 group z-10 md:static absolute left-1/2 -translate-x-1/2 md:translate-x-0"
        >
          <div className="relative flex items-center justify-center w-8 h-8 md:w-12 md:h-12 overflow-hidden">
            <img 
              src="/images/nova-logo.webp" 
              alt="Literafan Logo" 
              width="48"
              height="48"
              fetchPriority="high"
              decoding="async"
              className="w-6 h-6 md:w-10 md:h-10 object-contain relative z-10 group-hover:scale-110 transition-transform duration-500"
            />
          </div>
          <span className="font-serif text-lg md:text-2xl font-bold tracking-tight text-stone-100 drop-shadow-md">
            Litera<span className="text-primary italic">fan</span>
          </span>
        </Link>

        {/* Desktop Menu */}
        <nav className="hidden md:flex items-center space-x-2">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              onClick={(e) => handleLinkClick(e, link.path)}
              className={`px-5 py-2 rounded-full font-sans text-sm tracking-wide transition-all duration-300 relative group border ${
                isActive(link.path) 
                  ? 'neon-active' 
                  : 'text-stone-300 bg-transparent border-transparent hover:text-white hover:bg-white/5'
              }`}
            >
              {link.name}
            </Link>
          ))}
        </nav>

        {/* CTA & Share & Socials */}
        <div className="flex items-center justify-end gap-2 md:gap-3 absolute right-4 md:static">
          {/* Sino de Notificações Web Push */}
          <WebPushBell />

          <Link 
            to="/comunidade" 
            onClick={(e) => handleLinkClick(e, '/comunidade')}
            className="hidden md:inline-flex items-center justify-center px-5 py-2 bg-gradient-to-r from-red-700 via-primary to-stone-900 border border-red-500/80 text-white text-xs font-bold rounded-full hover:from-red-600 hover:to-black hover:scale-105 transition-all duration-500 shadow-[0_0_18px_rgba(255,50,50,0.5),inset_0_0_8px_rgba(255,50,50,0.25)] hover:shadow-[0_0_30px_rgba(255,50,50,0.85),inset_0_0_12px_rgba(255,50,50,0.4)]"
          >
            {t('nav.join_community', 'Comunidade')}
          </Link>

          <div className="hidden xl:flex items-center gap-3 ml-2 border-l border-white/10 pl-5">
            <a href="https://www.youtube.com/@literafan" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-red-500 transition-all hover:scale-110" title="YouTube" aria-label="Canal do Literafan no YouTube">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            </a>
            <a href="https://www.instagram.com/literafan?igsh=eDhxejBkbDZlb2ty" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-pink-500 transition-all hover:scale-110" title="Instagram" aria-label="Perfil do Literafan no Instagram">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            </a>
            <a href="https://www.tiktok.com/@literafan_?_t=ZM-8timJV3t3J7&_r=1" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-white transition-all hover:scale-110" title="TikTok" aria-label="Perfil do Literafan no TikTok">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93v7.2c0 1.96-.5 3.96-1.72 5.39-1.16 1.36-2.95 2.18-4.83 2.18-1.95 0-3.71-.85-4.87-2.26-1.2-1.45-1.68-3.46-1.48-5.38.2-1.9 1.25-3.66 2.82-4.72 1.52-1.03 3.42-1.38 5.22-1.07V13.5c-1.2-.14-2.45.15-3.4.85-.94.69-1.48 1.8-1.48 2.96 0 1.15.54 2.25 1.48 2.94.95.7 2.2.99 3.4.85 1.15-.14 2.18-.8 2.74-1.8.56-1 .82-2.18.82-3.35V.02z"/></svg>
            </a>
            <a href="https://www.facebook.com/profile.php?id=61554102537888" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-blue-500 transition-all hover:scale-110" title="Facebook" aria-label="Página do Literafan no Facebook">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/></svg>
            </a>
            <a href="https://x.com/literafan_" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-white transition-all hover:scale-110" title="X (Twitter)" aria-label="Perfil do Literafan no X antigo Twitter">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a href="https://www.twitch.tv/literafan_" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-purple-500 transition-all hover:scale-110" title="Twitch" aria-label="Canal do Literafan na Twitch">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/></svg>
            </a>
            <a href="https://kick.com/literafan" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-green-500 transition-all hover:scale-110 flex items-center justify-center" title="Kick" aria-label="Canal do Literafan na Kick">
              <div 
                className="w-[22px] h-[22px] bg-current" 
                style={{ 
                  WebkitMaskImage: 'url(https://i.postimg.cc/nztcj49c/logo-kick-4.png)', 
                  WebkitMaskSize: 'contain', 
                  WebkitMaskRepeat: 'no-repeat', 
                  WebkitMaskPosition: 'center', 
                  maskImage: 'url(https://i.postimg.cc/nztcj49c/logo-kick-4.png)', 
                  maskSize: 'contain', 
                  maskRepeat: 'no-repeat', 
                  maskPosition: 'center' 
                }}
              />
            </a>
            <a href="https://discord.gg/dQrr92QpM" target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-[#5865F2] transition-all hover:scale-110" title="Discord" aria-label="Comunidade Literafan no Discord">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994.021-.041.001-.09-.041-.106a13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.929 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.894.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
            </a>
          </div>

          {/* Member Profile Avatar (Extreme Right) */}
          {currentUser ? (
            <Link
              to="/perfil"
              onClick={(e) => handleLinkClick(e, '/perfil')}
              className="relative group flex items-center ml-2"
              title={currentUser.displayName ? `Perfil de ${currentUser.displayName}` : 'Meu Perfil'}
            >
              {currentUser.photoURL ? (
                <img 
                  src={currentUser.photoURL} 
                  alt={currentUser.displayName || 'Perfil'} 
                  width="36"
                  height="36"
                  decoding="async"
                  className="w-8 h-8 md:w-9 md:h-9 rounded-full object-cover border-2 border-primary/80 hover:border-primary transition-all shadow-md group-hover:scale-105"
                />
              ) : (
                <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-primary/25 border-2 border-primary/80 text-primary flex items-center justify-center font-bold text-xs shadow-md group-hover:scale-105 transition-all">
                  {currentUser.displayName?.charAt(0).toUpperCase() || 'M'}
                </div>
              )}
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-black" title="Inscrito" />
            </Link>
          ) : null}
          
          {(location.pathname.startsWith('/artigo/') || location.pathname.startsWith('/artigos/')) && (
            <button 
              onClick={handleShare} 
              className="flex items-center justify-center w-10 h-10 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-stone-300 hover:text-white transition-colors focus:outline-none"
              title="Compartilhar Artigo"
            >
              <Upload size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Reading Progress Bar (logo abaixo da Navbar ao ler um artigo) */}
      <ReadingProgressBar previewMode={previewMode} activePath={activePath} />
    </header>
  );
};

export default Navbar;